import { ApiService } from '../utils/ApiService';
import { debugLog } from '../utils/debug';
import { AuthService } from '../auth/AuthService';
import { TokenManager } from '../auth/TokenManager';
import { DeviceFingerprint } from '../utils/DeviceFingerprint';
import {
  CellIndicator,
  CellError,
  formatCellError,
  formatCellErrorMatrix,
  getCellErrorForApiError,
  getCellErrorForResult,
  isAuthError,
} from '../constants/ErrorMessages';
import type { FormulaParameterValue } from '../types/formulas';
import { excelErrorLike, withExcelErrors, withExcelErrorsMatrix } from './excelErrors';
import { mayHoldCellUnion, resolveCellUnions } from './cellUnions';
import { isExcelError } from './cellUnionParsing';
import { workbookDateSystem } from '../services/workbookDateSystem';

interface PendingFormula {
  id: string;
  formula: string;
  functionType: string;
  parameters: FormulaParameterValue[];
  resolve: (value: number | string) => void;
  reject: (error: Error) => void;
}

interface BatchResult {
  id: string;
  result: number | null;
  error?: string;
  code?: string;
}

const BATCH_WINDOW_MS = 300;
const MAX_BATCH_SIZE = 1200;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
// Long-poll: the add-in holds one in-flight progress request (continuous I/O)
// so the Excel custom-functions runtime can't suspend mid-fetch and orphan the
// cells at #BUSY. The server holds each request up to LONG_POLL_WAIT_SECONDS,
// returning immediately once the batch is done; the client loops back-to-back.
const LONG_POLL_WAIT_SECONDS = 20;
const LONG_POLL_REQUEST_TIMEOUT_MS = 30000;
const MAX_POLL_DURATION_MS = 180000;
const POLL_RETRY_DELAY_MS = 500;
// Floor per loading iteration — guards against a busy loop if a server ever
// returns loading without actually holding the request (e.g. ?wait not honored).
const MIN_LONG_POLL_INTERVAL_MS = 750;

let apiServiceInstance: ApiService | null = null;
let authServiceInstance: AuthService | null = null;
let tokenManagerInstance: TokenManager | null = null;
let deviceFingerprintInstance: DeviceFingerprint | null = null;

const pendingFormulas: Map<string, PendingFormula> = new Map();
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let formulaCounter = 0;
let cacheWarmed = false;


// Remotely-toggleable breadcrumbs for diagnosing the custom-functions runtime
// on real devices (debugLog is compiled out in production). Default OFF — prod
// is silent until a superadmin enables CF debug logging; the resolved flag rides
// on the /formulas/batch response metadata and is applied before any cfLog runs.
// Prefixed for console filtering.
let cfDebugEnabled = false;
export function setCfDebugLogging(enabled: boolean): void {
  cfDebugEnabled = enabled;
}
function cfLog(...args: unknown[]): void {
  if (cfDebugEnabled) {
    console.log('[BIFROST-CF]', ...args);
  }
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

let sandboxModeActive = false;
export function isSandboxMode(): boolean { return sandboxModeActive; }

// On globalThis: the task pane sets it from its own copy of this module (see preCalcState).
const forceRefreshHolder = globalThis as unknown as { __bifrostForceRefresh?: boolean };
export function setForceRefresh(value: boolean): void { forceRefreshHolder.__bifrostForceRefresh = value; }

const PRECALC_TTL_MS = 30000;

/**
 * Keys an earlier build persisted the cache under. Nothing writes them now;
 * they are only removed, so installs that had them stop keeping old figures
 * (error strings included) on disk.
 */
const LEGACY_PRECALC_KEYS = ['bifrost_precalc', 'bifrost_precalc_expiry', 'bifrost_precalc_scope'];

/**
 * The add-in's only result cache: values the task pane just fetched, held in
 * memory for PRECALC_TTL_MS so the recalc it triggers reads them instead of
 * asking again. Never persisted.
 *
 * On globalThis rather than in module scope because taskpane.html loads the
 * functions bundle and the task-pane bundle side by side in the shared
 * runtime, each with its own copy of this module. Module state would let the
 * task pane write or clear its copy while the cells kept reading the other one.
 */
interface PreCalcState {
  values: Record<string, number | string>;
  expiry: number;
  scope: string | null;
  /** Bumped by every clear, so a fetch that started before one cannot write after it. */
  generation: number;
  /** In-flight requests by formula key, so identical cells share one. */
  pending: Map<string, Promise<number | string>>;
  /** Requests ever started, so the pane can show done-of-started without going backwards. */
  started: number;
}

function preCalcState(): PreCalcState {
  const holder = globalThis as unknown as { __bifrostPreCalc?: PreCalcState };
  if (!holder.__bifrostPreCalc) {
    holder.__bifrostPreCalc = { values: {}, expiry: 0, scope: null, generation: 0, pending: new Map(), started: 0 };
  }
  return holder.__bifrostPreCalc;
}

let scopeRead: Promise<string | null> | null = null;

/**
 * Whose values these are. A value cached for one company must never answer a
 * cell in another. Concurrent callers share one read, since a recalc asks for
 * every cell at once.
 */
function currentScope(): Promise<string | null> {
  if (!scopeRead) {
    scopeRead = (async () => {
      try {
        const user = await getTokenManager().getUserInfo();
        return user?.userId && user.companyId ? `${user.userId}:${user.companyId}` : null;
      } catch {
        return null;
      }
    })().finally(() => { scopeRead = null; });
  }
  return scopeRead;
}

/** Taken before a fetch whose results are meant for the cache. */
export interface PreCalcTicket {
  scope: string | null;
  generation: number;
}

export async function beginPreCalc(): Promise<PreCalcTicket> {
  return { scope: await currentScope(), generation: preCalcState().generation };
}

/**
 * Replace the cached results with a fresh set, if they still belong to the
 * session that asked for them. Returns false, writing nothing, when the user
 * or company changed or the cache was cleared while the fetch was running —
 * the caller must then not write those results anywhere else either.
 */
export async function setPreCalculatedResults(
  results: Map<string, number | string>,
  ticket: PreCalcTicket
): Promise<boolean> {
  const scope = await currentScope();
  const state = preCalcState();
  if (!ticket.scope || scope !== ticket.scope || state.generation !== ticket.generation) {
    return false;
  }
  const values: Record<string, number | string> = {};
  for (const [key, value] of results) {
    values[key] = value;
  }
  state.values = values;
  state.expiry = Date.now() + PRECALC_TTL_MS;
  state.scope = scope;
  return true;
}

/** Remove what an earlier build persisted. Safe to call before Office.js is up. */
export async function purgePersistedPreCalc(): Promise<void> {
  try {
    if (typeof OfficeRuntime === 'undefined' || !OfficeRuntime.storage) return;
    await OfficeRuntime.storage.removeItems(LEGACY_PRECALC_KEYS);
  } catch {
    // Nothing persisted, or storage not available on this host.
  }
}

/**
 * Drop every cached result, so the next calculation goes to the server.
 *
 * Requests already in flight are forgotten, so a new cell starts its own
 * request. One still running answers its cell only if the same user and
 * company are signed in when it finishes.
 */
export async function clearPreCalculatedResults(): Promise<void> {
  const state = preCalcState();
  state.values = {};
  state.expiry = 0;
  state.scope = null;
  state.generation++;
  state.pending.clear();
  await purgePersistedPreCalc();
}

async function readPreCalculated(key: string): Promise<number | string | undefined> {
  const state = preCalcState();
  if (state.expiry <= Date.now()) return undefined;
  const cached = state.values[key];
  if (cached === undefined) return undefined;
  const scope = await currentScope();
  if (!scope || scope !== state.scope) return undefined;
  return cached;
}

export function getFormulaKey(functionType: string, formula: string, parameters: FormulaParameterValue[]): string {
  return `${functionType}:${formula}:${JSON.stringify(parameters)}`;
}

const getApiService = () => {
  if (!apiServiceInstance) {
    apiServiceInstance = new ApiService();
  }
  return apiServiceInstance;
};

const getAuthService = () => {
  if (!authServiceInstance) {
    authServiceInstance = new AuthService();
  }
  return authServiceInstance;
};

const getTokenManager = () => {
  if (!tokenManagerInstance) {
    tokenManagerInstance = new TokenManager();
  }
  return tokenManagerInstance;
};

const getDeviceFingerprint = () => {
  if (!deviceFingerprintInstance) {
    deviceFingerprintInstance = new DeviceFingerprint();
  }
  return deviceFingerprintInstance;
};

/**
 * Decide what an auth-shaped error actually means.
 *
 * `ApiService` rewrites every 401 to "Sign in required", so a single blip
 * during a recalculation looks identical to a dead session. Clearing tokens on
 * sight wiped the refresh token too — both live in one stored blob — so a
 * momentary failure logged the user out of every cell in the workbook.
 *
 * Ask the server instead. `renewSession()` coalesces concurrent callers onto
 * one `/auth/refresh`, which matters here because a recalculation can 401
 * across many batches at once, and it clears the session only when the refresh
 * token is definitively rejected.
 *
 * Returns true when the caller should retry, false when the session is really
 * gone and `#SIGN_IN` is the honest answer.
 */
async function tryRenewSession(): Promise<boolean> {
  try {
    const renewed = await getAuthService().renewSession();
    debugLog(renewed ? 'Session renewed after auth error' : 'Session could not be renewed');
    return renewed;
  } catch (e) {
    // A renewal that throws is not evidence the session is dead; keep it and
    // let the next recalculation try again.
    console.warn('Session renewal failed:', e);
    return false;
  }
}

async function getAuthHeaders(): Promise<Record<string, string> | null> {
  try {
    const authService = getAuthService();
    const tokenManager = getTokenManager();
    const deviceFingerprint = getDeviceFingerprint();

    const isAuthenticated = await authService.isAuthenticated();
    const token = await tokenManager.getToken();
    const fingerprint = await deviceFingerprint.generate();

    if (!isAuthenticated || !token) {
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'x-device-fingerprint': fingerprint
    };
  } catch {
    return null;
  }
}

function scheduleBatchExecution(): void {
  if (pendingFormulas.size >= MAX_BATCH_SIZE) {
    executeBatch();
    return;
  }

  if (batchTimer) {
    clearTimeout(batchTimer);
  }

  batchTimer = setTimeout(() => {
    executeBatch();
  }, BATCH_WINDOW_MS);
}

async function executeBatch(): Promise<void> {
  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }

  const isForceRefresh = forceRefreshHolder.__bifrostForceRefresh === true;
  forceRefreshHolder.__bifrostForceRefresh = false;

  if (pendingFormulas.size === 0) {
    return;
  }

  const batch = new Map(pendingFormulas);
  pendingFormulas.clear();

  debugLog(`Executing batch of ${batch.size} formulas`);
  return sendBatch(batch, isForceRefresh, false);
}

/**
 * Send an already-drained batch. Separate from `executeBatch` so an auth
 * failure can be retried after renewing the session — the pending map has
 * been cleared by then, so re-entering `executeBatch` would send nothing.
 *
 * `isRetry` caps that at one attempt.
 */
async function sendBatch(
  batch: Map<string, PendingFormula>,
  isForceRefresh: boolean,
  isRetry: boolean
): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    if (!headers) {
      for (const [, pending] of batch) {
        pending.resolve(CellIndicator.SIGN_IN);
      }
      return;
    }

    if (!cacheWarmed) {
      cacheWarmed = true;
      const apiService = getApiService();
      apiService.post('/formulas/warm-cache', {}, { headers }).catch(() => {});
    }

    const formulas = Array.from(batch.values()).map(p => ({
      id: p.id,
      formula: p.formula,
      functionType: p.functionType,
      parameters: p.parameters,
    }));

    const apiService = getApiService();
    const response = await apiService.post<{
      results: BatchResult[];
      metadata?: Record<string, unknown>;
      loading?: boolean;
      batchId?: string;
      progress?: number;
      message?: string;
      partialResults?: BatchResult[];
      pendingSegments?: string[];
      retryAfterSeconds?: number;
    }>(
      '/formulas/batch',
      { formulas, forceRefresh: isForceRefresh || undefined, dateSystem: await workbookDateSystem() },
      { headers }
    );

    if (!response.success) {
      const errorMsg = response.error || CellError.BATCH_FAILED;

      if (isAuthError(errorMsg)) {
        if (await tryRenewSession() && !isRetry) {
          // One retry only. If the renewed token 401s too the session is not
          // the problem, and retrying again would spin the whole workbook.
          return sendBatch(batch, isForceRefresh, true);
        }

        for (const [, pending] of batch) {
          pending.resolve(CellIndicator.SIGN_IN);
        }
        return;
      }

      for (const [, pending] of batch) {
        pending.resolve(formatCellError(errorMsg));
      }
      return;
    }

    // Apply the remotely-set CF debug flag before any breadcrumb fires.
    const cfDebugFlag = response.data?.metadata?.cfDebugLogging;
    if (typeof cfDebugFlag === 'boolean') {
      setCfDebugLogging(cfDebugFlag);
    }

    // Background fetch in progress — hold a long-poll until results land.
    if (response.data?.loading === true) {
      const batchId = response.data.batchId;
      cfLog(`batch ${batchId ?? '?'} loading (${response.data.progress ?? 0}%) — long-polling`);

      // Resolve any cached partial results immediately.
      resolvePartialResults(response.data.partialResults, batch);

      if (batchId && batch.size > 0) {
        storeActiveBatchId(batchId);
        try {
          await pollUntilComplete(batchId, headers, batch);
        } finally {
          clearActiveBatch();
        }
      } else if (batch.size > 0) {
        resolveAll(batch, formatCellError(CellError.NO_RESULT));
      }

      return;
    }

    if (!response.data?.results) {
      for (const [, pending] of batch) {
        pending.resolve(formatCellError(CellError.NO_RESULTS));
      }
      return;
    }

    const resultsMap = new Map(response.data.results.map(r => [r.id, r]));

    for (const [id, pending] of batch) {
      const result = resultsMap.get(id);
      if (!result) {
        pending.resolve(formatCellError(CellError.NO_RESULT));
      } else if (result.error) {
        pending.resolve(formatCellError(getCellErrorForResult({ error: result.error, code: result.code })));
      } else if (result.result !== null) {
        pending.resolve(result.result);
      } else {
        // Neither a value nor an error: a failure, never a zero that reads as a figure.
        pending.resolve(formatCellError(CellError.NO_RESULT));
      }
    }

    const batchMetadata = response.data?.metadata as { sandboxMode?: boolean } | undefined;
    if (batchMetadata?.sandboxMode !== undefined) {
      sandboxModeActive = batchMetadata.sandboxMode;
    }

    debugLog(`Batch completed: ${batch.size} formulas in ${response.data.metadata?.executionTimeMs}ms`);
  } catch (error) {
    console.error('Batch execution failed:', error);
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';

    if (isAuthError(errorMsg)) {
      if (await tryRenewSession() && !isRetry) {
        return sendBatch(batch, isForceRefresh, true);
      }
      for (const [, pending] of batch) {
        pending.resolve(CellIndicator.SIGN_IN);
      }
    } else {
      for (const [, pending] of batch) {
        pending.resolve(formatCellError(errorMsg));
      }
    }
  }
}

async function storeActiveBatchId(batchId: string | null): Promise<void> {
  try {
    if (typeof OfficeRuntime !== 'undefined' && OfficeRuntime.storage) {
      if (batchId) {
        await OfficeRuntime.storage.setItem('bifrost_active_batch', JSON.stringify({
          batchId,
          timestamp: Date.now()
        }));
      } else {
        await OfficeRuntime.storage.removeItem('bifrost_active_batch');
      }
    }
  } catch (err) {
    console.warn('Failed to store batch ID:', err);
  }
}

async function clearActiveBatch(): Promise<void> {
  await storeActiveBatchId(null);
}

interface BatchProgressResponse {
  loading: boolean;
  progress: number;
  status: string;
  message?: string;
  completedFormulas?: number;
  totalFormulas?: number;
  results?: BatchResult[];
  partialResults?: BatchResult[];
  error?: string;
}

function resolvePartialResults(
  results: BatchResult[] | undefined,
  batch: Map<string, PendingFormula>
): number {
  if (!results || results.length === 0) return 0;
  let resolved = 0;
  for (const result of results) {
    const pending = batch.get(result.id);
    if (pending) {
      // A result with neither a value nor an error is a failure, never a zero:
      // a 0 in a cell reads as a real figure.
      pending.resolve(result.error
        ? formatCellError(getCellErrorForResult({ error: result.error, code: result.code }))
        : (result.result ?? formatCellError(CellError.NO_RESULT)));
      batch.delete(result.id);
      resolved++;
    }
  }
  return resolved;
}

function resolveAll(batch: Map<string, PendingFormula>, value: number | string): void {
  for (const [, pending] of batch) {
    pending.resolve(value);
  }
  batch.clear();
}

// Inline long-poll loop. Each request is held open by the server (continuous
// active I/O), so the Excel custom-functions runtime stays alive instead of
// suspending and orphaning the cells at #BUSY. Resolves cells progressively as
// partial results arrive, and on terminal completion/error/timeout.
async function pollUntilComplete(
  batchId: string,
  headers: Record<string, string>,
  batch: Map<string, PendingFormula>
): Promise<void> {
  const apiService = getApiService();
  const deadline = Date.now() + MAX_POLL_DURATION_MS;
  let consecutiveFailures = 0;

  // Recursive (not a loop) so each long-poll is one continuous in-flight
  // request — keeps the custom-functions runtime alive without await-in-loop.
  const step = async (): Promise<void> => {
    if (batch.size === 0) return;
    if (Date.now() >= deadline) {
      cfLog(`batch ${batchId} overall timeout — resolving ${batch.size} cell(s) as TIMEOUT`);
      resolveAll(batch, formatCellError(CellError.TIMEOUT));
      return;
    }

    const iterStart = Date.now();
    try {
      const response = await apiService.get<BatchProgressResponse>(
        `/formulas/batch/${batchId}/progress?wait=${LONG_POLL_WAIT_SECONDS}`,
        { headers, timeout: LONG_POLL_REQUEST_TIMEOUT_MS }
      );

      if (!response.success) {
        const errorMsg = response.error || 'Unknown error';
        if (errorMsg.includes('not found') || errorMsg.includes('expired')) {
          cfLog(`batch ${batchId} not found/expired`);
          resolveAll(batch, formatCellError(CellError.BATCH_FAILED));
          return;
        }
        consecutiveFailures++;
        cfLog(`batch ${batchId} poll failed (${consecutiveFailures}/${MAX_CONSECUTIVE_POLL_FAILURES}): ${errorMsg}`);
        if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
          resolveAll(batch, formatCellError(CellError.NETWORK_ERROR));
          return;
        }
        await sleep(POLL_RETRY_DELAY_MS);
        return step();
      }

      consecutiveFailures = 0;
      const data = response.data;
      if (!data) {
        await sleep(POLL_RETRY_DELAY_MS);
        return step();
      }

      // Progressive fill: resolve anything ready so far.
      resolvePartialResults(data.partialResults, batch);

      if (!data.loading) {
        if (data.status === 'error' || data.error) {
          cfLog(`batch ${batchId} terminal error: ${data.error ?? 'unknown'}`);
          resolveAll(batch, formatCellError(data.error || CellError.BATCH_FAILED));
          return;
        }
        resolvePartialResults(data.results, batch);
        if (batch.size > 0) {
          cfLog(`batch ${batchId} complete but ${batch.size} formula(s) missing from results`);
          resolveAll(batch, formatCellError(CellError.NO_RESULT));
        } else {
          cfLog(`batch ${batchId} complete — all cells resolved`);
        }
        return;
      }

      cfLog(`batch ${batchId} still loading (${data.progress ?? 0}%), ${batch.size} pending`);

      const elapsed = Date.now() - iterStart;
      if (elapsed < MIN_LONG_POLL_INTERVAL_MS) {
        await sleep(MIN_LONG_POLL_INTERVAL_MS - elapsed);
      }
      return step();
    } catch (err) {
      consecutiveFailures++;
      cfLog(`batch ${batchId} poll request failed (${consecutiveFailures}/${MAX_CONSECUTIVE_POLL_FAILURES})`, err);
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        resolveAll(batch, formatCellError(CellError.NETWORK_ERROR));
        return;
      }
      await sleep(POLL_RETRY_DELAY_MS);
      return step();
    }
  };

  return step();
}

function registerFormula(functionType: string, formula: string, parameters: FormulaParameterValue[]): Promise<number | string> {
  const id = `f_${++formulaCounter}_${Date.now()}`;

  return new Promise((resolve, reject) => {
    pendingFormulas.set(id, {
      id,
      formula,
      functionType,
      parameters,
      resolve,
      reject,
    });

    scheduleBatchExecution();
  });
}

/**
 * Blank arguments must keep their position. Dropping them outright shifted
 * every later argument one slot left, so =BIFROST.AP("AP_BALANCE",,"2026","8")
 * arrived as vendor="2026", year="8" and quietly returned 0. Only trailing
 * blanks are safe to discard — nothing positional depends on them.
 */
function trimTrailingBlanks(params: FormulaParameterValue[]): FormulaParameterValue[] {
  let end = params.length;
  while (end > 0 && params[end - 1] === undefined) end--;
  return params.slice(0, end);
}

function normalizeParameter(param: FormulaParameterValue): FormulaParameterValue {
  if (param === undefined || param === null || param === '') {
    return undefined;
  }

  if (typeof param === 'number') {
    if (Number.isInteger(param) && param >= 2010 && param <= 2040) {
      console.warn(`Parameter ${param} looks like a year - if you meant a date like "2025-01", please use quotes around it in the formula`);
      return String(param);
    }
    return String(param);
  }

  return param;
}

/**
 * The arguments as the user wrote them: `E8+E7` becomes the union of the two
 * cells instead of their sum (numbers) or #VALUE! (text). An error argument that
 * is not such a union is passed back to Excel unchanged, as Excel itself would
 * have done before `allowErrorForDataTypeAny` let errors reach the function.
 */
/** An argument's own error, to reach the cell as it is rather than as a Bifrost error. */
class PassThroughError {
  constructor(readonly error: unknown) {}
}

/** A `cell+cell` argument that could not be read; computing with Excel's sum would query the wrong code. */
class UnreadableCells {}

async function argumentsAsWritten(
  raw: unknown[],
  invocation: { address?: string } | undefined
): Promise<FormulaParameterValue[]> {
  let resolved = raw;
  if (invocation?.address && mayHoldCellUnion(raw)) {
    const outcome = await resolveCellUnions(invocation.address, raw);
    if (outcome.kind === 'unreadable') throw new UnreadableCells();
    if (outcome.kind === 'rewritten') resolved = outcome.params;
  }
  const error = resolved.find(isExcelError);
  if (error) throw new PassThroughError(excelErrorLike(error));
  return resolved as FormulaParameterValue[];
}

function createFunctionHandler(functionType: string) {
  return async function (
    formulaName: string,
    p1?: FormulaParameterValue, p2?: FormulaParameterValue, p3?: FormulaParameterValue, p4?: FormulaParameterValue, p5?: FormulaParameterValue,
    p6?: FormulaParameterValue, p7?: FormulaParameterValue, p8?: FormulaParameterValue, p9?: FormulaParameterValue, p10?: FormulaParameterValue,
    p11?: FormulaParameterValue, p12?: FormulaParameterValue, p13?: FormulaParameterValue, p14?: FormulaParameterValue,
    invocation?: { address?: string }
  ): Promise<number | string> {
    try {
      if (!formulaName) {
        return formatCellError(CellError.FORMULA_NAME_REQUIRED);
      }

      const params = trimTrailingBlanks(
        (await argumentsAsWritten([p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12, p13, p14], invocation))
          .map(normalizeParameter)
      );

      const key = getFormulaKey(functionType, formulaName, params);

      const cached = await readPreCalculated(key);
      if (cached !== undefined) return cached;

      const state = preCalcState();
      // Shared per user and company only: a request made for one session must
      // never answer a cell for another.
      const scope = await currentScope();
      const pendingKey = `${scope ?? ''}|${key}`;

      const existingPromise = state.pending.get(pendingKey);
      if (existingPromise) {
        return existingPromise;
      }

      // A switch, a sign-out or a token renewal that lands in the home company
      // can happen while the request runs; its figure then belongs to the
      // session that asked, and the cell gets a recalculate message instead.
      const promise = registerFormula(functionType, formulaName, params).then(async (value) =>
        ((await currentScope()) === scope ? value : formatCellError(CellError.SESSION_CHANGED)));
      state.pending.set(pendingKey, promise);
      state.started = (state.started ?? 0) + 1;

      promise.finally(() => {
        // Only its own entry: a clear may have replaced it with a newer request.
        if (state.pending.get(pendingKey) === promise) state.pending.delete(pendingKey);
      });

      return await promise;
    } catch (error) {
      if (error instanceof PassThroughError) throw error.error;
      if (error instanceof UnreadableCells) return formatCellError(CellError.CELLS_UNREADABLE);
      console.error('Formula execution error:', error);
      return formatCellError(getCellErrorForApiError({ message: error instanceof Error ? error.message : String(error) }));
    }
  };
}

/**
 * One function for every aggregate, and one for every drilldown.
 *
 * There used to be four of each — LEDGER/BUDGET/AR/AP and their _ENTRIES
 * variants — but the type was never used: execute takes
 * `{ formulaName, parameters }`, batch takes `{ id, formula, parameters }` and
 * drilldown takes `{ formula, parameters, limit, offset }`. None of them reads
 * it. The backend resolves the formula by name, which is unique per company,
 * and the stored definition carries its own functionType.
 *
 * So the four names were four spellings of one call, and unvalidated with it:
 * BIFROST.AR("BALANCE") happily returned a ledger value. The type still lives
 * in the database, where it groups formulas in the portal; it just no longer
 * has to be repeated in the cell.
 */
const ledger = withExcelErrors(createFunctionHandler('ledger'));

function createEntriesFunctionHandler(functionType: string) {
  return async function (
    formulaName: string,
    p1?: FormulaParameterValue, p2?: FormulaParameterValue, p3?: FormulaParameterValue, p4?: FormulaParameterValue, p5?: FormulaParameterValue,
    p6?: FormulaParameterValue, p7?: FormulaParameterValue, p8?: FormulaParameterValue, p9?: FormulaParameterValue, p10?: FormulaParameterValue,
    p11?: FormulaParameterValue, p12?: FormulaParameterValue, p13?: FormulaParameterValue, p14?: FormulaParameterValue,
    invocation?: { address?: string }
  ): Promise<(string | number | null)[][]> {
    try {
      if (!formulaName) {
        return formatCellErrorMatrix(CellError.FORMULA_NAME_REQUIRED);
      }

      const params = trimTrailingBlanks(
        (await argumentsAsWritten([p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12, p13, p14], invocation))
          .map(normalizeParameter)
      );

      const apiService = getApiService();
      const requestDrilldown = async () => {
        const headers = await getAuthHeaders();
        if (!headers) return null;
        return apiService.post<{
          rows: (string | number | null)[][];
          totalCount: number;
          hasMore: boolean;
        }>(
          '/formulas/drilldown',
          {
            formula: formulaName,
            functionType,
            parameters: params,
            limit: 5000,
            offset: 0,
            dateSystem: await workbookDateSystem(),
          },
          { headers }
        );
      };

      let response = await requestDrilldown();
      if (!response) {
        return [[CellIndicator.SIGN_IN]];
      }

      // A 401 here is usually an access token that aged out mid-session, not a
      // dead session. Renew and send it again once before giving up.
      if (!response.success && isAuthError(response.error || '')) {
        if (!(await tryRenewSession())) {
          return [[CellIndicator.SIGN_IN]];
        }
        const retried = await requestDrilldown();
        if (!retried) {
          return [[CellIndicator.SIGN_IN]];
        }
        response = retried;
      }

      if (!response.success) {
        const errorMsg = response.error || 'Drilldown failed';
        if (isAuthError(errorMsg)) {
          return [[CellIndicator.SIGN_IN]];
        }
        return formatCellErrorMatrix(errorMsg);
      }

      const rows = response.data?.rows;
      // A 1×0 matrix ([[]]) renders as #CALC! in Excel because there are
      // no cells to spill into. PR #17 fixes the backend to never ship
      // this shape, but we keep the defensive check here in case any
      // other caller ever produces it.
      if (!rows || rows.length === 0 || (Array.isArray(rows[0]) && rows[0].length === 0)) {
        return [['No data']];
      }

      const result: (string | number | null)[][] = [];
      for (let i = 0; i < rows.length; i++) {
        const srcRow = rows[i];
        const newRow: (string | number | null)[] = [];
        for (let j = 0; j < srcRow.length; j++) {
          newRow.push(srcRow[j]);
        }
        result.push(newRow);
      }

      // Say so when the result was cut short. Silently spilling the first N of
      // a much larger set produces a total that looks right and doesn't
      // reconcile — the worst way for this to fail.
      const total = response.data?.totalCount;
      if (response.data?.hasMore && typeof total === 'number') {
        const shown = result.length - 1; // first row is the header
        const note: (string | number | null)[] = new Array(result[0]?.length ?? 1).fill(null);
        note[0] = `Showing ${shown} of ${total} rows — narrow the filters to see the rest`;
        result.push(note);
      }
      return result;
    } catch (error) {
      if (error instanceof PassThroughError) throw error.error;
      if (error instanceof UnreadableCells) return formatCellErrorMatrix(CellError.CELLS_UNREADABLE);
      console.error('Entries formula execution error:', error);
      return formatCellErrorMatrix(getCellErrorForApiError({ message: error instanceof Error ? error.message : String(error) }));
    }
  };
}

const entries = withExcelErrorsMatrix(createEntriesFunctionHandler('ledger'));

export { ledger, entries };
